import React from "react";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import {
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from "../../components/common/appShell";

import "./deviceGroups.css";

const DEFAULT_PAGE_SIZE = 25;

const EMPTY_DEVICE_GROUPS_META = {
  currentPage: 1,
  limit: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  totalPages: 0,
  totalDeviceGroups: 0,
  searchMatchedCount: 0,
  sortBy: "devicecount",
  sortDir: "desc",
  summary: {
    groupCount: 0,
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    assignedUsers: 0,
  },
};

const SORT_OPTIONS = [
  { label: "Most devices", value: "deviceCount_desc" },
  { label: "Fewest devices", value: "deviceCount_asc" },
  { label: "Most offline devices", value: "offlineCount_desc" },
  { label: "Fewest offline devices", value: "offlineCount_asc" },
  { label: "Most active alerts", value: "activeAlertCount_desc" },
  { label: "Fewest active alerts", value: "activeAlertCount_asc" },
  { label: "Group name A-Z", value: "name_asc" },
  { label: "Group name Z-A", value: "name_desc" },
];

export const getUserIdentityKey = (user = {}, fallbackIndex = 0) => {
  if (user?.id !== undefined && user?.id !== null && user.id !== "") {
    return `id:${user.id}`;
  }
  if (user?.email_id) {
    return `email:${String(user.email_id).toLowerCase()}`;
  }
  return `unknown:${fallbackIndex}`;
};

export const countUniqueUsers = (users = []) => {
  const seenUsers = new Set();

  (Array.isArray(users) ? users : []).forEach((user, index) => {
    seenUsers.add(getUserIdentityKey(user, index));
  });

  return seenUsers.size;
};

const formatNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString();
  }

  return value;
};

const formatActiveAlertCount = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return "—";
  }

  return formatNumber(numericValue);
};

const getSortParams = (sortOption) => {
  const [sortBy = "deviceCount", sortDir = "desc"] = String(sortOption || "").split("_");
  return {
    sortBy,
    sortDir: sortDir === "asc" ? "asc" : "desc",
  };
};

const getActiveAlertBadgeMeta = (value, { totalDevices } = {}) => {
  if (Number(totalDevices) === 0) {
    return {
      label: "No devices",
      tone: "empty",
    };
  }

  if (value === null || value === undefined) {
    return {
      label: "No alert access",
      tone: "restricted",
    };
  }

  const formattedValue = formatActiveAlertCount(value);
  if (formattedValue === "—") {
    return {
      label: formattedValue,
      tone: "restricted",
    };
  }

  const numericValue = Number(value);
  if (numericValue > 0) {
    return {
      label: `${formattedValue} active`,
      tone: "active",
    };
  }

  return {
    label: "No alerts",
    tone: "clear",
  };
};

const isAdminUser = (user = {}) =>
  Boolean(user?.is_enterprise_admin || user?.has_admin_privileges);

const canUseAnyRolePermission = (user = {}, resources = {}, field) => {
  if (!user || Object.keys(user).length === 0) {
    return true;
  }

  if (isAdminUser(user)) {
    return true;
  }

  const roles = [
    user?.role,
    ...(Array.isArray(resources?.groups)
      ? resources.groups.map((group) => group?.role)
      : []),
  ].filter(Boolean);

  if (!user?.role && !resources?.groupsLoaded) {
    return false;
  }

  if (roles.length === 0) {
    return true;
  }

  return roles.some((role) => role?.[field] === true);
};

export default class DeviceGroups extends React.Component {
  deviceGroupsRequestId = 0;

  componentDidMount() {
    this.fetchDeviceGroups();
  }

  state = {
    searchBar: "",
    currentFilter: "",
    sortOption: SORT_OPTIONS[0].value,
    groups: [],
    meta: EMPTY_DEVICE_GROUPS_META,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    groupsLoading: true,
    groupsLoaded: false,
    groupsError: null,
  };

  buildQueryParams = (page = this.state.page) => {
    const params = {
      page,
      limit: this.state.pageSize,
    };

    if (this.state.currentFilter.trim()) {
      params.search = this.state.currentFilter.trim();
    }

    const sortParams = getSortParams(this.state.sortOption);
    params.sortBy = sortParams.sortBy;
    params.sortDir = sortParams.sortDir;

    return params;
  };

  fetchDeviceGroups = (page = this.state.page) => {
    const requestId = this.deviceGroupsRequestId + 1;
    this.deviceGroupsRequestId = requestId;

    this.setState({
      groupsLoading: true,
      groupsError: null,
    });

    axios
      .get("/v2/groups", {
        params: this.buildQueryParams(page),
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
      .then((res) => {
        if (requestId !== this.deviceGroupsRequestId) {
          return;
        }

        this.setState({
          groups: Array.isArray(res.data?.data) ? res.data.data : [],
          meta: {
            ...EMPTY_DEVICE_GROUPS_META,
            ...(res.data?.meta || {}),
            summary: {
              ...EMPTY_DEVICE_GROUPS_META.summary,
              ...(res.data?.meta?.summary || {}),
            },
          },
          page,
          groupsLoading: false,
          groupsLoaded: true,
        });
      })
      .catch((error) => {
        if (requestId !== this.deviceGroupsRequestId) {
          return;
        }

        this.setState({
          groupsLoading: false,
          groupsLoaded: true,
          groupsError: error,
        });
      });
  };

  applyFilters = () => {
    this.setState({
      currentFilter: this.state.searchBar.trim(),
      page: 1,
    }, () => {
      this.fetchDeviceGroups(1);
    });
  };

  clearFilters = () => {
    this.setState({
      searchBar: "",
      currentFilter: "",
      page: 1,
    }, () => {
      this.fetchDeviceGroups(1);
    });
  };

  handleSearchChange = (event) => {
    this.setState({ searchBar: event.target.value });
  };

  handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.applyFilters();
    }
  };

  handleSortChange = (event) => {
    this.setState({
      sortOption: event.target.value,
      page: 1,
    }, () => {
      this.fetchDeviceGroups(1);
    });
  };

  handlePageChange = ({ selected }) => {
    const nextPage = selected + 1;
    this.setState({ page: nextPage }, () => {
      this.fetchDeviceGroups(nextPage);
    });
  };

  render() {
    const { resources = {} } = this.props;
    const {
      groups,
      groupsLoading,
      groupsLoaded,
      groupsError,
      meta,
      sortOption,
      page,
      pageSize,
    } = this.state;
    const filteredStats = {
      ...EMPTY_DEVICE_GROUPS_META.summary,
      ...(meta.summary || {}),
    };
    const hasSearch = Boolean(this.state.currentFilter);

    const isAdmin =
      this.props.user.has_admin_privileges ||
      this.props.user.is_enterprise_admin;
    const permissionResources = {
      ...resources,
      groups,
      groupsLoaded: groupsLoaded || resources.groupsLoaded,
    };
    const showActiveAlerts =
      groups.some(
        (group) =>
          group.activeAlertCount !== null && group.activeAlertCount !== undefined,
      ) ||
      canUseAnyRolePermission(
        this.props.user,
        permissionResources,
        "viewEventInfo",
      );

    const totalPages = Number(meta.totalPages) || 0;
    const totalCount = Number(meta.totalCount) || 0;
    const totalDeviceGroups = Number(meta.totalDeviceGroups) || totalCount;
    const showingStart = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
    const showingEnd = totalCount > 0
      ? Math.min(showingStart + groups.length - 1, totalCount)
      : 0;

    const header = (
      <div className="device-groups-header">
        <div>
          <h2 className="device-groups-header__title">
            Manage power distribution unit groups
          </h2>
          <div className="device-groups-header__description">
            Organize PDUs into groups, assign access to the right teammates, and
            monitor fleet connectivity from one streamlined workspace.
          </div>
        </div>
        <div className="device-groups-header__actions">
          <Link
            to="/devices/connected"
            className="button secondary button--icon-label device-groups-header__action"
          >
            <i className="material-icons" aria-hidden="true">dns</i>
            <span>See all PDUs</span>
          </Link>
          {isAdmin && (
            <Link
              to="/devices/groups/new"
              className="button button--icon-label device-groups-header__action"
            >
              <i className="material-icons" aria-hidden="true">add</i>
              <span>Create group</span>
            </Link>
          )}
        </div>
      </div>
    );

    if (groupsLoading && !groupsLoaded) {
      return (
        <div className="device-groups-page">
          {header}
          <div className="device-groups-summary">
            {[1, 2, 3, 4].map((card) => (
              <div
                key={card}
                className="device-groups-summary-card device-groups-skeleton-card"
              >
                <div className="skeleton-blank" style={{ width: "40%" }} />
                <div
                  className="skeleton-blank"
                  style={{ width: "70%", height: "24px" }}
                />
                <div className="skeleton-blank" style={{ width: "55%" }} />
                <div className="device-groups-summary-card__accent" />
              </div>
            ))}
          </div>
          <div className="device-groups-table-wrapper">
            <div className="device-groups-table">
              {[1, 2, 3].map((row) => (
                <div
                  key={row}
                  className="device-groups-skeleton-row"
                  style={{ borderTop: row === 1 ? "none" : undefined }}
                >
                  {[1, 2, 3, 4, 5, 6].map((cell) => (
                    <div key={cell}>
                      <div
                        className="skeleton-blank"
                        style={{ width: cell === 1 ? "80%" : "60%" }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (groupsError) {
      return (
        <div className="device-groups-page">
          {header}
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">error_outline</i>
            <div className="device-groups-empty__title">
              Device groups could not be loaded
            </div>
            <div className="device-groups-empty__description">
              Refresh the page or try again in a moment.
            </div>
            <div className="device-groups-empty__actions">
              <button
                type="button"
                className="device-groups-table__manage"
                onClick={() => this.fetchDeviceGroups(1)}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (groups.length === 0 && !hasSearch) {
      const emptyDescription = isAdmin
        ? "Device groups allow you to organize PDUs, assign users, and control permissions at scale. Create your first group to start managing devices together."
        : "Device groups help your organization organize PDUs and control access. Check back soon or contact an administrator for access.";

      return (
        <div className="device-groups-page">
          {header}
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">group_add</i>
            <div className="device-groups-empty__title">No device groups yet</div>
            <div className="device-groups-empty__description">
              {emptyDescription}
            </div>
            {isAdmin && (
              <div className="device-groups-empty__actions">
                <Link to="/devices/groups/new" className="button">
                  Create new group
                </Link>
              </div>
            )}
          </div>
        </div>
      );
    }

    const { searchBar } = this.state;

    const summaryCards = [
      {
        key: "groups",
        label: "Groups",
        value: formatNumber(filteredStats.groupCount),
        meta: hasSearch
          ? `of ${formatNumber(totalDeviceGroups)} total`
          : "Total device groups",
        accentColor: "var(--text-soft-color)",
      },
      {
        key: "online",
        label: "Online devices",
        value: formatNumber(filteredStats.onlineDevices),
        meta: filteredStats.totalDevices
          ? `out of ${formatNumber(filteredStats.totalDevices)} unique ${
              filteredStats.totalDevices === 1 ? "device" : "devices"
            }`
          : "No devices assigned yet",
        accentColor: "var(--success-color)",
      },
      {
        key: "offline",
        label: "Offline devices",
        value: formatNumber(filteredStats.offlineDevices),
        meta: filteredStats.totalDevices
          ? `out of ${formatNumber(filteredStats.totalDevices)} unique ${
              filteredStats.totalDevices === 1 ? "device" : "devices"
            }`
          : "No devices in selection",
        accentColor: "var(--danger-color)",
      },
      {
        key: "members",
        label: "Assigned members",
        value: formatNumber(filteredStats.assignedUsers),
        meta:
          filteredStats.assignedUsers === 1
            ? "Unique team member across visible groups"
            : "Unique team members across visible groups",
        accentColor: "var(--quinary-color)",
      },
    ];

    return (
      <div className="device-groups-page">
        {header}

        <div className="device-groups-summary">
          {summaryCards.map((card) => (
            <div
              key={card.key}
              className="device-groups-summary-card"
              style={{ "--accent-color": card.accentColor }}
            >
              <div className="device-groups-summary-card__value">{card.value}</div>
              <div className="device-groups-summary-card__label">{card.label}</div>
              <div className="device-groups-summary-card__meta">{card.meta}</div>
              <div className="device-groups-summary-card__accent" />
            </div>
          ))}
        </div>

        <AppToolbar variant="plain" className="device-groups-toolbar">
          <AppToolbarGrid wide>
            <AppToolbarField label="Search groups">
              <div className="app-toolbar__search">
                <input
                  type="text"
                  placeholder="Search by group name or description"
                  value={searchBar}
                  onChange={this.handleSearchChange}
                  onKeyDown={this.handleSearchKeyDown}
                />
                <button
                  type="button"
                  className="search-icon-button device-groups-search__button"
                  onClick={this.applyFilters}
                  aria-label="Search device groups"
                  disabled={groupsLoading}
                >
                  <i className="material-icons">search</i>
                </button>
              </div>
            </AppToolbarField>
            <AppToolbarField label="Sort by">
              <select
                value={sortOption}
                onChange={this.handleSortChange}
                disabled={groupsLoading}
              >
                {SORT_OPTIONS.map((option) => {
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </AppToolbarField>
            <AppToolbarField label="Results">
              <div className="app-toolbar__meta device-groups-toolbar__meta">
                {groupsLoading && groupsLoaded
                  ? "Updating groups..."
                  : totalCount === 0
                    ? "No groups match your search"
                    : `Showing ${formatNumber(showingStart)}-${formatNumber(
                        showingEnd,
                      )} of ${formatNumber(totalCount)} groups`}
              </div>
            </AppToolbarField>
          </AppToolbarGrid>
          {hasSearch ? (
            <div className="device-groups-active-filters">
              {this.state.currentFilter ? (
                <span className="device-groups-filter-pill">
                  Search: {this.state.currentFilter}
                </span>
              ) : null}
              <button
                type="button"
                className="device-groups-filter-clear"
                onClick={this.clearFilters}
                disabled={groupsLoading}
              >
                Clear search
              </button>
            </div>
          ) : null}
        </AppToolbar>

        {groups.length === 0 ? (
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">search</i>
            <div className="device-groups-empty__title">
              No groups match your search
            </div>
            <div className="device-groups-empty__description">
              Try adjusting your search to see all available device groups.
            </div>
            <div className="device-groups-empty__actions">
              <button
                type="button"
                className="device-groups-table__manage"
                onClick={this.clearFilters}
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="device-groups-table-wrapper">
            <div
              className={`device-groups-table ${
                showActiveAlerts ? "" : "device-groups-table--no-alerts"
              }`}
            >
              <div className="device-groups-table__head">
                <div>Group</div>
                <div>Devices</div>
                <div>Connectivity</div>
                {showActiveAlerts ? <div>Active Alerts</div> : null}
                <div>Users</div>
                <div style={{ textAlign: "right" }}>Actions</div>
              </div>
              {groups.map((groupObj) => {
                const totalDevices = Array.isArray(groupObj.items)
                  ? groupObj.items.length
                  : Number(groupObj.itemsCount) || 0;
                const onlineDevices = Number(groupObj.itemsOnline) || 0;
                const offlineDevices = Math.max(totalDevices - onlineDevices, 0);
                const onlinePercent = totalDevices
                  ? Math.round((onlineDevices / totalDevices) * 100)
                  : 0;
                const description = groupObj.description?.trim()
                  ? groupObj.description
                  : "No description provided";
                const userCount = countUniqueUsers(groupObj.users);
                const activeAlertCount = groupObj.activeAlertCount;
                const activeAlertBadge = getActiveAlertBadgeMeta(activeAlertCount, {
                  totalDevices,
                });
                const activeAlertBadgeClassName = `device-groups-alert-badge device-groups-alert-badge--${activeAlertBadge.tone}`;

                return (
                  <div className="device-groups-table__row" key={groupObj.id}>
                    <div className="device-groups-table__name">
                      <Link to={`/devices/groups/${groupObj.id}`}>
                        {groupObj.name}
                      </Link>
                      <div className="device-groups-table__description">
                        {description}
                      </div>
                    </div>
                    <div className="device-groups-table__metric">
                      <strong>{formatNumber(totalDevices)}</strong>
                      <span>Total devices</span>
                      <div className="device-groups-table__bar">
                        <div
                          className="device-groups-table__bar-fill"
                          style={{ width: `${onlinePercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="device-groups-table__metric">
                      <div className="device-groups-table__status-online">
                        {formatNumber(onlineDevices)} online
                      </div>
                      <div className="device-groups-table__status-offline">
                        {formatNumber(offlineDevices)} offline
                      </div>
                    </div>
                    {showActiveAlerts ? (
                      <div className="device-groups-table__alerts">
                        <span className={activeAlertBadgeClassName}>
                          {activeAlertBadge.label}
                        </span>
                      </div>
                    ) : null}
                    <div className="device-groups-table__users">
                      <strong>{formatNumber(userCount)}</strong>
                      <span>
                        {userCount === 1 ? "Assigned member" : "Assigned members"}
                      </span>
                    </div>
                    <div className="device-groups-table__actions">
                      <Link
                        to={`/devices/groups/${groupObj.id}`}
                        className="device-groups-table__manage"
                      >
                        View group
                      </Link>
                      {isAdmin && (
                        <Link
                          to={`/devices/groups/${groupObj.id}/settings`}
                          className="device-groups-table__manage"
                        >
                          Manage
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {totalPages > 1 ? (
          <div className="device-groups-pagination">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={Math.max(0, page - 1)}
              pageCount={totalPages}
              onPageChange={this.handlePageChange}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
            />
          </div>
        ) : null}
      </div>
    );
  }
}
